import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { sound } from '../game/sound';
import { useSettings, type ControlSize, type ControlScheme, type ReducedMotionSetting } from '../settings';

export interface SettingsModalProps {
  onClose: () => void;
}

const CONTROL_SIZE_LABELS: Record<ControlSize, string> = {
  regular: 'Regular',
  large: 'Large',
  xlarge: 'Extra large',
};

const CONTROL_SCHEME_LABELS: Record<ControlScheme, string> = {
  joystick: 'Joystick',
  dpad: 'D-pad',
  swipe: 'Swipe only',
};

// PART 4: 'auto' reads as "match device" -- the setting a first-time
// player actually recognizes, not the internal token name.
const REDUCED_MOTION_LABELS: Record<ReducedMotionSetting, string> = {
  auto: 'Match device',
  on: 'On',
  off: 'Off',
};

// TURN-INTENT TRACE: capture-build only -- see main.tsx's TRACE_BUILD doc
// comment. Gates the "Turn-intent tracing" settings row below `TRACE_BUILD
// && (...)` so a plain/prod build can dead-code-eliminate the whole row
// (its label text, testid, and note) rather than merely hiding it.
const TRACE_BUILD = import.meta.env.VITE_ENABLE_TRACE === '1';

/** Settings, persisted to localStorage via `settings.ts` (haptics, control
 * size, controls mode) and the existing `sound` singleton (mute) -- one
 * source of truth per setting, read/written the same way everywhere
 * (SettingsModal, MuteButton, useGameLayout/DPad all read the SAME
 * underlying store, never a locally-duplicated copy). */
export function SettingsModal({ onClose }: SettingsModalProps): JSX.Element {
  const [settings, setSettings] = useSettings();
  const [muted, setMutedState] = useState(sound.muted);

  useEffect(() => sound.subscribe(setMutedState), []);

  return (
    <Modal title="Settings" onClose={onClose} className="settings-modal" testId="settings-modal">
      <div className="settings-row">
        <label className="settings-label" htmlFor="settings-sound">
          Sound
        </label>
        <button
          type="button"
          id="settings-sound"
          role="switch"
          aria-checked={!muted}
          className={`btn btn-toggle${!muted ? ' btn-toggle-on' : ''}`}
          onClick={() => sound.setMuted(!muted)}
        >
          {muted ? 'Off' : 'On'}
        </button>
      </div>

      <div className="settings-row">
        <label className="settings-label" htmlFor="settings-haptics">
          Haptics
        </label>
        <button
          type="button"
          id="settings-haptics"
          role="switch"
          aria-checked={settings.haptics}
          className={`btn btn-toggle${settings.haptics ? ' btn-toggle-on' : ''}`}
          onClick={() => setSettings({ haptics: !settings.haptics })}
        >
          {settings.haptics ? 'On' : 'Off'}
        </button>
      </div>

      <div className="settings-row settings-row-col">
        <span className="settings-label">Control size</span>
        <div className="settings-choices" role="radiogroup" aria-label="Control size">
          {(Object.keys(CONTROL_SIZE_LABELS) as ControlSize[]).map((size) => (
            <button
              key={size}
              type="button"
              role="radio"
              aria-checked={settings.controlSize === size}
              className={`chip${settings.controlSize === size ? ' chip-active' : ''}`}
              onClick={() => setSettings({ controlSize: size })}
            >
              {CONTROL_SIZE_LABELS[size]}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-row settings-row-col">
        <span className="settings-label">Controls</span>
        <div className="settings-choices" role="radiogroup" aria-label="Controls">
          {(Object.keys(CONTROL_SCHEME_LABELS) as ControlScheme[]).map((scheme) => (
            <button
              key={scheme}
              type="button"
              role="radio"
              aria-checked={settings.controlScheme === scheme}
              className={`chip${settings.controlScheme === scheme ? ' chip-active' : ''}`}
              onClick={() => setSettings({ controlScheme: scheme })}
            >
              {CONTROL_SCHEME_LABELS[scheme]}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-row">
        <label className="settings-label" htmlFor="settings-colorblind">
          Colorblind mode
        </label>
        <button
          type="button"
          id="settings-colorblind"
          role="switch"
          aria-checked={settings.colorblind}
          className={`btn btn-toggle${settings.colorblind ? ' btn-toggle-on' : ''}`}
          onClick={() => setSettings({ colorblind: !settings.colorblind })}
        >
          {settings.colorblind ? 'On' : 'Off'}
        </button>
      </div>

      <div className="settings-row settings-row-col">
        <span className="settings-label">Reduced motion</span>
        <div className="settings-choices" role="radiogroup" aria-label="Reduced motion">
          {(Object.keys(REDUCED_MOTION_LABELS) as ReducedMotionSetting[]).map((rm) => (
            <button
              key={rm}
              type="button"
              role="radio"
              aria-checked={settings.reducedMotion === rm}
              className={`chip${settings.reducedMotion === rm ? ' chip-active' : ''}`}
              onClick={() => setSettings({ reducedMotion: rm })}
            >
              {REDUCED_MOTION_LABELS[rm]}
            </button>
          ))}
        </div>
      </div>

      {TRACE_BUILD && (
        <div className="settings-row">
          <label className="settings-label" htmlFor="settings-debug-joystick">
            Turn-intent tracing
          </label>
          <button
            type="button"
            id="settings-debug-joystick"
            role="switch"
            aria-checked={settings.debugJoystick}
            className={`btn btn-toggle${settings.debugJoystick ? ' btn-toggle-on' : ''}`}
            onClick={() => setSettings({ debugJoystick: !settings.debugJoystick })}
          >
            {settings.debugJoystick ? 'On' : 'Off'}
          </button>
        </div>
      )}
      {TRACE_BUILD && settings.debugJoystick && (
        <p className="dim" data-testid="settings-debug-joystick-note">
          Adds a "Missed a turn" button during play. Takes effect on your next reconnect.
        </p>
      )}
    </Modal>
  );
}
